import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const categories = [
  { name: 'Film', slug: 'film' },
  { name: 'Music', slug: 'music' },
  { name: 'TV', slug: 'tv' },
  { name: 'Sports', slug: 'sports' },
  { name: 'Business', slug: 'business' },
];

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      const res = await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email,
        source: 'footer'
      });
      if (res.data.status === 'existing') {
        toast.info('You\'re already subscribed!');
      } else if (res.data.status === 'reactivated') {
        toast.success('Welcome back! Your subscription has been reactivated.');
      } else {
        toast.success('Thanks for subscribing!');
      }
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-foreground text-background">
      {/* Newsletter Section */}
      <div className="border-b border-background/10">
        <div className="container-custom py-12">
          <div className="max-w-xl mx-auto text-center">
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4">
              Stay in the loop
            </h3>
            <p className="text-background/70 mb-6">
              Get the latest entertainment news delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/10 border-background/20 text-background placeholder:text-background/50"
                required
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                variant="secondary"
                disabled={subscribing}
                data-testid="newsletter-submit-btn"
              >
                {subscribing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-4" data-testid="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_contentcms-2/artifacts/fo9a7wfg_Dream%2BBigger.%2BBuild%2BSmarter.%2BOwn%2BMore..webp" 
                alt="The Trade - Dream Bigger. Build Smarter. Own More." 
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-background/70 text-sm mt-4">
              Your source for entertainment news, music updates, sports coverage, and movie reviews.
            </p>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest mb-4 text-background/50">
              Categories
            </h4>
            <ul className="space-y-2">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-background/70 hover:text-background transition-colors text-sm"
                    data-testid={`footer-category-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest mb-4 text-background/50">
              Company
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                  data-testid="footer-about-link"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                  data-testid="footer-contact-link"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/submit"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                  data-testid="footer-submit-link"
                >
                  Submit Content
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest mb-4 text-background/50">
              Legal
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/privacy"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                  data-testid="footer-terms-link"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/accessibility"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                  data-testid="footer-accessibility-link"
                >
                  Accessibility
                </Link>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest mb-4 text-background/50">
              Follow Us
            </h4>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/thetrade.media?igsh=NTc4MTIwNjQ2YQ=="
                target="_blank"
                rel="noopener noreferrer"
                className="text-background/70 hover:text-background transition-colors"
                aria-label="Follow us on Instagram"
                data-testid="social-instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@thetrade.media?_r=1&_t=ZP-93GtcZTJd2l"
                target="_blank"
                rel="noopener noreferrer"
                className="text-background/70 hover:text-background transition-colors"
                aria-label="Follow us on TikTok"
                data-testid="social-tiktok"
              >
                <TikTokIcon className="h-5 w-5" />
              </a>
              <a
                href="https://youtube.com/@thetrade-media?si=b5EW10sx53OFtHo1"
                target="_blank"
                rel="noopener noreferrer"
                className="text-background/70 hover:text-background transition-colors"
                aria-label="Subscribe on YouTube"
                data-testid="social-youtube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-background/10">
        <div className="container-custom py-6">
          <p className="text-center text-background/50 text-sm">
            © {new Date().getFullYear()} The Trade Media. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
